import React, { FC, useCallback, useMemo } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { makeStyles } from "@material-ui/core";
import Autocomplete from "../../../../components/Autocomplete";
import BootstrapInput from "../../../../components/BootstrapInput";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import { IWizardStepContent } from "../../../wizard/types";
import useBillingQueries from "../../../billing/hooks/useBillingQueries";
import { useNotify } from "../../../notify/useNotify";
import { mutateErrors } from "../../../../utils";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { IFile } from "../../../../components/UploadFile/types";
import { currencies } from "../../../../utils/const/currencies";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
  },
  subContainer: {
    "& + &": {
      marginTop: 16,
    },
  },
}));

const Content: FC<IWizardStepContent> = ({ handleSubmit }) => {
  const classes = useStyles();
  const { values } = useFormState();
  const { change } = useForm();
  const { uploadReceiptImage } = useBillingQueries();
  const snackNotify = useNotify();

  const currencyList = useMemo(
    () =>
      currencies.map((item) => ({
        id: item.id,
        name: item.code,
      })),
    [],
  );

  const receiptImage: IFile[] = useMemo(
    () => (values.receiptImage ? [values.receiptImage] : []),
    [values.receiptImage],
  );

  const addNewFiles = useCallback(async (files) => {
    for (const file of files) {
      try {
        let { data } = await uploadReceiptImage(file);

        change("receiptImage", data);
        snackNotify(
          "Success",
          `${file.name} has been uploaded.`,
          NotifyMode.SUCCESS,
        );
      } catch (e) {
        const { message } = mutateErrors(e);
        snackNotify("Error", `${file.name}`, NotifyMode.ERROR);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFileHandler = useCallback((file) => {
    change("receiptImage", undefined);
  }, []);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className={classes.container}>
        <div className={classes.subContainer}>
          <Field
            required
            fullWidth
            type="text"
            label="Amount"
            placeholder="Only numbers"
            name={"amount"}
            component={BootstrapInput}
          />
        </div>
        <div className={classes.subContainer}>
          <Field
            required
            fullWidth
            label="Currency"
            options={currencyList}
            placeholder="Please Select"
            infoIcon={"Choose currency you require"}
            name={"currency"}
            component={Autocomplete}
          />
        </div>
        <div className={classes.subContainer}>
          <UploadFile
            uploadedFiles={receiptImage}
            addNewFiles={addNewFiles}
            removeFile={removeFileHandler}
            titleText={"Upload Your Receipt"}
            allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
            maxSize={30}
          />
        </div>
      </div>
    </form>
  );
};

export default Content;
